import Accessor from '@arcgis/core/core/Accessor';
import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';
import { whenDefinedOnce, whenTrueOnce } from '@arcgis/core/core/watchUtils';
import MapView from "@arcgis/core/views/MapView";
import SceneView from "@arcgis/core/views/SceneView";
import EsriMap from "@arcgis/core/Map";

import MyAppsViewModel from '../../data/MyApps/MyAppsViewModel';
import ItemLoaderViewModel from '../../data/ItemLoader/ItemLoaderViewModel';
import AppTypeInfoViewModel from '../../data/AppTypeInfo/AppTypeInfoViewModel';
import PublishAppViewModel from '../../data/PublishApp/PublishAppViewModel';
import QueryStringViewModel from '../../data/QueryString/QueryStringViewModel';

import { CompatibilityChecker, EAppTemplateType } from 'templates-common-library/baseClasses/CompatibilityChecker';
import AppTypeTile, { AppTypeTileProperties, EResourceType, resourceItem } from '../../../app/widgets/AppTypeTile';

import appMessagesT9N from "../../../t9n/App/App.json";
import { findAppTypeBadge } from '../../utils/appUtils';

import Telemetry from "@esri/telemetry";
import NavBarViewModel from '../NavBar/NavBarViewModel';
import AlertWrapper from '../../widgets/AlertWrapper';
import Collection from '@arcgis/core/core/Collection';

const FEATURED_TYPE_KEYWORD = "InstantAppGallery:Featured";


@subclass('Landing.ui.ContentArea.ContentAreaViewModel')
export default class ContentAreaViewModel extends Accessor {
	@property()
	view: __esri.MapView | __esri.SceneView;

	@property()
	portal: __esri.Portal;

	@property()
	telemetry: Telemetry;

	@property()
	itemLoaderVM: ItemLoaderViewModel = new ItemLoaderViewModel();

	@property()
	myAppsDataVM: MyAppsViewModel = new MyAppsViewModel();

	@property()
	appTypeInfoVM: AppTypeInfoViewModel = new AppTypeInfoViewModel();

	@property()
	publishAppVM: PublishAppViewModel = new PublishAppViewModel();

	@property()
	queryStringVM: QueryStringViewModel = new QueryStringViewModel();

	@property()
	notCompatible: Map<EAppTemplateType, string>;

	@property()
	appMessages: typeof appMessagesT9N;

	@property()
	activeAlerts: __esri.Collection<AlertWrapper> = new Collection();

	@property()
	navbarVM: NavBarViewModel;

	@property()
	browseAllTiles: AppTypeTile[];

	private _compatibilityChecker: CompatibilityChecker;

	constructor(params?: any) {
		super(params);

		// Populate DataVMs with portal
		whenDefinedOnce(this, "portal", () => {
			this.itemLoaderVM.portal = this.portal;
			this.myAppsDataVM.portal = this.portal;
			this.appTypeInfoVM.portal = this.portal;
			this.queryStringVM.portal = this.portal;

			this.publishAppVM.portal = this.portal;
			this.publishAppVM.navbarVM = this.navbarVM;
			this.publishAppVM.telemetry = this.telemetry;
			this.publishAppVM.myAppsDataVM = this.myAppsDataVM;

			this.navbarVM.telemetry = this.telemetry;
			this.navbarVM.itemLoaderVM = this.itemLoaderVM;
			this.navbarVM.myAppsDataVM = this.myAppsDataVM;

			// Coordinate any other interactions that need to happen between the DataVMs
			whenDefinedOnce(this.queryStringVM, ["defaultTab", "defaultAppId"], () => {
				this.navbarVM.setNavbarRoute(this.queryStringVM.defaultTab);

			});
		});


		whenDefinedOnce(this, "appMessages", () => {
			this.publishAppVM.appMessages = this.appMessages;
			const { resourceMessages, requirementsMessages } = this.appMessages;
			// Not Compat Class Instantiate
			this._compatibilityChecker = new CompatibilityChecker({
				resourceMessages,
				requirementsMessages
			});
		});
	}

	createMap(dom: HTMLDivElement): void {
		whenTrueOnce(this.itemLoaderVM, "isItemLoaderDone", () => {
			switch (this.itemLoaderVM.loadedItemType) {
				case "webmap":
					this._handleWebmap(dom);
					break;
				case "webscene":
					this._handleWebscene(dom);
					break;
				case "group":
					this._handleGroup(dom);
					break;
				case "none":
					this._handleNoLoadedItem(dom);
					break;
			}
		});
	}

	/**
	 * Takes in the PortalItem for the AppType and formats the data for ease of use
	 * @param item - PortalItem representing the AppType (ex: Item for Attachment Viewer, Item for Media... etc)  
	 */
	formatAppTypeItems(item: __esri.PortalItem, index: number): AppTypeTileProperties {
		const templateType: EAppTemplateType = item.url as EAppTemplateType;
		const resourceItem: resourceItem = this?.itemLoaderVM?.webmap?.portalItem || this?.itemLoaderVM?.webscene?.portalItem || this?.itemLoaderVM?.group;
		return {
			uniqueTileId: `${index}`,
			resourceInfo: {
				resourceItem,
				resourceType: this._findResourceType(resourceItem)
			},
			contentAreaVM: this,
			publishAppVM: this.publishAppVM,
			appTypeInfo: {
				screenshotUrl: item.screenshots[0] != null
					? `${this.publishAppVM.orgUrl}/sharing/rest/content/items/${item.id}/info/${item.screenshots[0]}`
					: item.thumbnailUrl, // fall back to thumbnail if scrshot undefined
				thumbnailUrl: item.thumbnailUrl,
				created: item.created,
				badge: findAppTypeBadge(item),
				appTypeTitle: this._titleFilter(item.title),
				appTypeUrlFragment: templateType,
				appTypeUrl: `${this.publishAppVM.orgUrl}${item.url}`.replace("http://", "https://"),
				compatibleMsgs: this.notCompatible?.get(templateType),
				appTypeId: item.id,
				portalItem: item
			}
		};
	}

	filterFeaturedApps(item: __esri.PortalItem): boolean {
		const index = item.typeKeywords.findIndex((keyword: string) => {
			return keyword === FEATURED_TYPE_KEYWORD;
		});

		return index !== -1;
	}

	createView(originalData: unknown[]): unknown[] {
		if (originalData != null) {
			return [...originalData];
		} else {
			return [];
		}
	}

	/** Configure Alert Notification properly */
	showAlert(title: string, msg: string, type: "success" | "error", goToLinkId?: string) {

		const alert = new AlertWrapper({
			title,
			msg,
			type,
			goToLinkId
		});

		this.activeAlerts.add(alert);

		whenTrueOnce(alert, "isDestroyed", (isDestroyed: boolean) => {
			if (isDestroyed) { this.activeAlerts.remove(alert); }
		});
	}

	/**
	 * Disables all zoom gestures on the given view instance.
	 *
	 * @param {esri/views/MapView} view - The MapView instance on which to
	 *                                  disable zooming gestures.
	 */
	private _disableZooming(view) {
		view.popup.dockEnabled = true;

		// Removes the zoom action on the popup
		view.popup.actions = [];

		// stops propagation of default behavior when an event fires
		function stopEvtPropagation(event) {
			event.stopPropagation();
		}

		// disable mouse wheel scroll zooming on the view
		view.on("mouse-wheel", stopEvtPropagation);

		// disable zooming via double-click on the view
		view.on("double-click", stopEvtPropagation);

		// disable zooming out via double-click + Control on the view
		view.on("double-click", ["Control"], stopEvtPropagation);

		// disables pinch-zoom and panning on the view
		view.on("drag", stopEvtPropagation);

		// disable the view's zoom box to prevent the Shift + drag
		// and Shift + Control + drag zoom gestures.
		view.on("drag", ["Shift"], stopEvtPropagation);
		view.on("drag", ["Shift", "Control"], stopEvtPropagation);

		// prevents zooming with the + and - keys
		view.on("key-down", function (event) {
			const prohibitedKeys = [
				"+",
				"-",
				"Shift",
				"_",
				"=",
				"ArrowUp",
				"ArrowDown",
				"ArrowRight",
				"ArrowLeft"
			];
			const keyPressed = event.key;
			if (prohibitedKeys.indexOf(keyPressed) !== -1) {
				event.stopPropagation();
			}
		});

		return view;
	}

	/** Filter everything past "(" out of Title string */
	private _titleFilter(title: string): string {
		const endPosition: number = title.indexOf("(");
		return title.substring(0, endPosition !== -1 ? endPosition : undefined).trim();
	}

	private _handleWebmap(dom: HTMLDivElement): void {
		const webmap = this.itemLoaderVM.webmap;

		this.view = new MapView({
			container: dom,
			map: webmap
		});
		const { view } = this;
		view.ui.components = ["attribution"];

		view.when(() => {
			view.goTo(view.extent);
			this._disableZooming(view);
		});

		whenTrueOnce(webmap, "loaded", async () => {
			this.notCompatible = await this._compatibilityChecker.checkAllTemplates(webmap);
		});
	}

	private _handleWebscene(dom: HTMLDivElement): void {
		const webscene = this.itemLoaderVM.webscene;

		this.view = new SceneView({
			container: dom,
			map: webscene,
		});
		const { view } = this;
		view.ui.components = ["attribution"];

		view.when(() => {
			view.goTo(view.extent);
			this._disableZooming(view);
		});

		whenTrueOnce(webscene, "loaded", async () => {
			this.notCompatible = await this._compatibilityChecker.checkAllTemplates(webscene);
		});
	}

	private async _handleGroup(dom: HTMLDivElement): Promise<void> {
		const map = new EsriMap({
			basemap: "satellite",
			ground: "world-elevation"
		});

		this.view = new SceneView({
			container: dom,
			map: map,
			scale: 500000000,
			camera: {
				position: [0, 0, 40000000] as any,
				heading: -23.5
			}
		});
		this.view.ui.components = ["attribution"];

		this.notCompatible = await this._compatibilityChecker.checkAllTemplates("group");
	}

	private _handleNoLoadedItem(dom: HTMLDivElement): void {
		const map = new EsriMap({
			basemap: this.portal.isPortal ? this.portal.defaultBasemap : "satellite"
		});

		this.view = new SceneView({
			container: dom,
			map: map,
			scale: 500000000,
			camera: {
				position: [0, 0, 40000000] as any,
				heading: -23.5
			}
		});
		this.view.ui.components = ["attribution"];

		// *** Disabling rotation due to high cpu usage ***
		// this.view.when(() => {
		// 	whenFalseOnce(this.view, "updating", this._rotate.bind(this));
		// });
	}

	private _rotate() {
		const camera = (this.view as __esri.SceneView).camera.clone();
		camera.position.longitude -= 0.05;
		this.view.goTo(camera, { animate: false });
		requestAnimationFrame(this._rotate.bind(this));
	}

	private _findResourceType(resourceItem: resourceItem): EResourceType {
		const resourceUnknown: any = resourceItem;
		if (resourceUnknown == null) {
			return EResourceType.None;
		} else if (resourceUnknown.fetchMembers != null) {
			return EResourceType.Group;
		} else if (resourceUnknown.type === "Web Map") {
			return EResourceType.Webmap;
		} else if (resourceUnknown.type === "Web Scene") {
			return EResourceType.Webscene;
		} else {
			return EResourceType.Unknown;
		}

	}

}



